/* global define */
/* eslint-disable no-var, object-shorthand */
/**
 * Shared color helpers for the conversational theme.
 *
 * Consumed by the builder appearance editor, which normalizes every color the
 * owner picks or types and uses the WCAG math for its plain-language contrast
 * warning, and by the front-end theme model, which normalizes the stored colors
 * before painting them.
 *
 * Nothing here substitutes one color for another. Owner-configured colors
 * render exactly as chosen; the product's obligation is guidance at
 * configuration time, which is why the contrast math answers which direction
 * would help rather than what to paint instead.
 */
define( 'shared/contrast',[], function () {
	/**
	 * Normalize a 3- or 6-digit hex string to a lowercase #rrggbb value.
	 *
	 * Owners type into the builder's hex field freely, so a leading `#` is
	 * optional and shorthand is expanded. An empty string means
	 * "unparseable" and is how callers reject a half-typed value.
	 *
	 * @param {string} value Raw color value.
	 * @return {string} Normalized #rrggbb hex, or '' when unparseable.
	 */
	function normalize( value ) {
		value = String( value || '' ).trim();
		if (
			/^[0-9a-f]{3}$/i.test( value ) ||
			/^[0-9a-f]{6}$/i.test( value )
		) {
			value = '#' + value;
		}
		if ( /^#[0-9a-f]{3}$/i.test( value ) ) {
			value = value.replace(
				/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i,
				'#$1$1$2$2$3$3'
			);
		}

		return /^#[0-9a-f]{6}$/i.test( value ) ? value.toLowerCase() : '';
	}

	/**
	 * Convert a hex color to its RGB channels, defaulting to black.
	 *
	 * @param {string} hex Color value.
	 * @return {Object} Object with r, g, b integer channels.
	 */
	function hexToRgb( hex ) {
		var value = normalize( hex ) || '#000000';
		return {
			r: parseInt( value.slice( 1, 3 ), 16 ),
			g: parseInt( value.slice( 3, 5 ), 16 ),
			b: parseInt( value.slice( 5, 7 ), 16 ),
		};
	}

	/**
	 * Compute the WCAG relative luminance of a color.
	 *
	 * Not exported: it exists for contrastRatio, and a helper nothing calls is
	 * a claim about a consumer that does not exist.
	 *
	 * @param {string} hex Color value.
	 * @return {number} Relative luminance in the 0..1 range.
	 */
	function relativeLuminance( hex ) {
		var rgb = hexToRgb( hex );
		var channels = [ rgb.r, rgb.g, rgb.b ].map( function ( channel ) {
			var srgb = channel / 255;
			return srgb <= 0.03928
				? srgb / 12.92
				: Math.pow( ( srgb + 0.055 ) / 1.055, 2.4 );
		} );
		return (
			0.2126 * channels[ 0 ] +
			0.7152 * channels[ 1 ] +
			0.0722 * channels[ 2 ]
		);
	}

	/**
	 * Compute the WCAG contrast ratio between two colors.
	 *
	 * @param {string} a First color.
	 * @param {string} b Second color.
	 * @return {number} Contrast ratio (1..21).
	 */
	function contrastRatio( a, b ) {
		var first = relativeLuminance( a );
		var second = relativeLuminance( b );
		var lighter = Math.max( first, second );
		var darker = Math.min( first, second );
		return ( lighter + 0.05 ) / ( darker + 0.05 );
	}

	/**
	 * Say whether black or white contrasts better with a surface.
	 *
	 * This answers a question about the surface, not about the owner's text
	 * color: the builder asks it to phrase its warning as "try something
	 * lighter" or "try something darker". It is deliberately not a substitution
	 * — no caller replaces a configured color with what this returns. White
	 * wins an exact tie, which happens only on a surface sitting equally far
	 * from both.
	 *
	 * @param {string} surface The color the text is painted on.
	 * @return {string} '#ffffff' or '#000000', whichever contrasts better.
	 */
	function betterOn( surface ) {
		return contrastRatio( '#ffffff', surface ) >=
			contrastRatio( '#000000', surface )
			? '#ffffff'
			: '#000000';
	}

	return {
		normalize: normalize,
		hexToRgb: hexToRgb,
		contrastRatio: contrastRatio,
		betterOn: betterOn,
	};
} );

